import { useEffect, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Check,
  CircleAlert,
  Database,
  FileDown,
  LogOut,
  Mail,
  MapPin,
  Phone,
  Play,
  Radar,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  approveMandateOpportunity,
  downloadMandateTourPdf,
  getMandateAudit,
  getMandateCommune,
  getMandateMap,
  getMandateOpportunities,
  getMandateOverview,
  getMandatePige,
  getMandateWorkflow,
  logoutDashboard,
  rejectMandateOpportunity,
  runMandateWorkflow,
  sendMandateDigest,
  setMandatePipeline,
} from "../lib/api";

const PIPELINE_STAGES = [
  ["contacte", "Contacté"],
  ["rendez_vous", "Rendez-vous"],
  ["mandat_signe", "Mandat signé"],
];
import { CommuneDetail } from "./CommuneDetail";
import { MandateMap } from "./MandateMap";
import { PigeListings } from "./PigeListings";
import { CardQrSection } from "./CardQrSection";

const formatPhone = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return digits.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
  return value;
};

const formatDate = (value) => {
  if (!value) return "Aucune donnée collectée";
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(value),
  );
};

const StatusBadge = ({ status }) => (
  <span className={`mandate-status mandate-status-${status}`} data-testid={`node-status-${status}`}>
    {status === "disabled" ? "Désactivé" : status === "error" ? "À vérifier" : "Prêt"}
  </span>
);

const OpportunityList = ({ isLoading, opportunities, highlightId, onApprove, onReject, onPipeline }) => {
  const [rejectingId, setRejectingId] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!isLoading && highlightId) {
      document.getElementById(`opportunity-${highlightId}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [isLoading, highlightId]);

  if (isLoading) return <p data-testid="dashboard-loading">Chargement sécurisé…</p>;

  if (opportunities.length === 0) {
    return (
      <div className="mandate-empty" data-testid="dashboard-empty-opportunities">
        <ShieldCheck size={24} />
        <p>Aucune opportunité qualifiée pour ce seuil.</p>
        <span>Les sources publiques restent traçables ; aucun profil personnel n’est inféré.</span>
      </div>
    );
  }

  return (
    <div className="mandate-opportunity-list" data-testid="dashboard-opportunity-list">
      {opportunities.map((item) => (
        <article
          key={`${item.source}-${item.source_record_id}`}
          id={`opportunity-${item.signal_id}`}
          className={item.signal_id === highlightId ? "mandate-opportunity-highlighted" : ""}
          data-testid={`dashboard-opportunity-${item.source_record_id}`}
        >
          <div>
            <span className={`mandate-score mandate-score-${item.tier}`}>{item.opportunity_score}</span>
            <div>
              <strong>{item.commune || "Localisation publique"}</strong>
              {item.address ? (
                <small className="mandate-address" data-testid={`opportunity-list-address-${item.signal_id}`}>
                  <MapPin size={12} /> {item.address}
                  {!item.address_exact && <em className="mandate-address-approx">approx.</em>}
                </small>
              ) : null}
              <small>{item.source} · {formatDate(item.updated_at)}</small>
            </div>
            {item.kind === "pige" ? (
              <span
                className={`mandate-pige-badge${item.opportunity_score > 80 ? " mandate-pige-badge-hot" : ""}`}
                data-testid={`pige-active-badge-${item.signal_id}`}
              >
                <Phone size={13} /> PIGE ACTIVE
              </span>
            ) : null}
          </div>
          <p>{item.signals?.map((signal) => signal.label).join(" · ")}</p>
          {item.kind === "pige" && (
            <>
              <p className="mandate-pige-listing" data-testid={`pige-listing-info-${item.signal_id}`}>
                {item.titre}
                {item.prix ? ` · ${Number(item.prix).toLocaleString("fr-FR")} €` : ""}
                {item.surface ? ` · ${item.surface} m²` : ""}
                {item.property_type ? ` · ${item.property_type}` : ""}
              </p>
              {item.phone ? (
                <div className="mandate-pige-phone" data-testid={`pige-phone-${item.signal_id}`}>
                  <span>{formatPhone(item.phone)}</span>
                  <a href={`tel:${item.phone}`} data-testid={`pige-call-link-${item.signal_id}`}>
                    <Phone size={14} /> Cliquer pour appeler
                  </a>
                </div>
              ) : (
                <span className="mandate-pige-nophone" data-testid={`pige-nophone-${item.signal_id}`}>
                  {item.bloctel === "INSCRIT"
                    ? "Numéro masqué · vendeur inscrit à Bloctel (démarchage interdit)"
                    : "Numéro non communiqué dans l'annonce"}
                </span>
              )}
              {item.phone && !item.bloctel ? (
                <p className="mandate-bloctel-warning" data-testid={`bloctel-warning-${item.signal_id}`}>
                  <AlertTriangle size={13} /> Attention : statut Bloctel non communiqué — vérifiez avant d’appeler.
                </p>
              ) : null}
            </>
          )}
          <div className="mandate-opportunity-meta">
            <span>Probabilité 90 j. : {item.sale_probability_90_days}%</span>
            <a href={item.source_url} target="_blank" rel="noreferrer" data-testid={`source-link-${item.source_record_id}`}>
              Source <ArrowUpRight size={15} />
            </a>
          </div>
          {item.review_status === "pending_review" ? (
            rejectingId === item.signal_id ? (
              <div className="mandate-reject-form" data-testid={`reject-form-${item.signal_id}`}>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Motif du rejet (obligatoire, 5 caractères minimum)"
                  data-testid={`reject-reason-${item.signal_id}`}
                />
                <div>
                  <button onClick={() => { setRejectingId(null); setReason(""); }} data-testid={`reject-cancel-${item.signal_id}`}>
                    Annuler
                  </button>
                  <button
                    className="mandate-reject-confirm"
                    disabled={reason.trim().length < 5}
                    onClick={async () => {
                      const done = await onReject(item.signal_id, reason.trim());
                      if (done) { setRejectingId(null); setReason(""); }
                    }}
                    data-testid={`reject-confirm-${item.signal_id}`}
                  >
                    Confirmer le rejet
                  </button>
                </div>
              </div>
            ) : (
              <div className="mandate-opportunity-actions">
                <button className="mandate-approve-button" onClick={() => onApprove(item.signal_id)} data-testid={`approve-button-${item.signal_id}`}>
                  <Check size={14} /> Approuver
                </button>
                <button className="mandate-reject-button" onClick={() => { setRejectingId(item.signal_id); setReason(""); }} data-testid={`reject-button-${item.signal_id}`}>
                  <X size={14} /> Rejeter
                </button>
              </div>
            )
          ) : (
            <span className={`mandate-review-status mandate-review-${item.review_status}`} data-testid={`review-status-${item.signal_id}`}>
              {item.review_status === "alerted" ? "Approuvée · alerte envoyée" : `Rejetée${item.rejection_reason ? ` · ${item.rejection_reason}` : ""}`}
            </span>
          )}
          {item.review_status !== "rejected" && (
            <div className="mandate-pipeline" data-testid={`pipeline-${item.signal_id}`}>
              {PIPELINE_STAGES.map(([stageKey, stageLabel]) => (
                <button
                  key={stageKey}
                  className={item.pipeline_stage === stageKey ? "is-active" : ""}
                  onClick={() => onPipeline(item.signal_id, item.pipeline_stage === stageKey ? "aucun" : stageKey)}
                  data-testid={`pipeline-${stageKey}-${item.signal_id}`}
                >
                  {stageLabel}
                </button>
              ))}
            </div>
          )}
        </article>
      ))}
    </div>
  );
};

const AuditLog = ({ entries }) => (
  <section className="mandate-audit-section" data-testid="mandate-audit-section">
    <div className="mandate-section-heading">
      <div>
        <p className="mandate-overline">Traçabilité</p>
        <h2>Journal d’audit</h2>
      </div>
    </div>
    {entries.length === 0 ? (
      <div className="mandate-empty" data-testid="audit-empty">
        <ShieldCheck size={22} />
        <p>Aucune action enregistrée pour le moment.</p>
        <span>Chaque approbation et chaque rejet sont journalisés ici (qui, quand, pourquoi).</span>
      </div>
    ) : (
      <div className="mandate-audit-list" data-testid="audit-entry-list">
        {entries.map((entry, index) => (
          <article key={`${entry.opportunity_id}-${entry.timestamp}-${index}`} data-testid={`audit-entry-${index}`}>
            <span className={`mandate-audit-action mandate-audit-${entry.action}`}>
              {entry.action === "approve" ? "Approuvée" : "Rejetée"}
            </span>
            <div>
              <strong>{entry.commune || entry.opportunity_id}</strong>
              <small>
                {entry.admin_email} · {formatDate(entry.timestamp)}
                {entry.reason && ` · Motif : ${entry.reason}`}
              </small>
            </div>
          </article>
        ))}
      </div>
    )}
  </section>
);

const WorkflowNodeList = ({ nodes }) => (
  <div>
    {nodes.map((node) => (
      <article key={node.key} data-testid={`workflow-node-${node.key}`}>
        <div>
          <span>{String(node.position).padStart(2, "0")}</span>
          <strong>{node.label}</strong>
          <small>{node.cadence === "daily" ? "Quotidien" : node.cadence === "every_2h" ? "Toutes les 2 h" : "À l’événement"}</small>
        </div>
        <StatusBadge status={node.status} />
      </article>
    ))}
  </div>
);

export default function MandateDashboard({ user, onLogout }) {
  const highlightId = new URLSearchParams(window.location.search).get("opportunite");
  const [minimumScore, setMinimumScore] = useState(60);
  const [overview, setOverview] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [isSendingDigest, setIsSendingDigest] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [mapCommunes, setMapCommunes] = useState([]);
  const [selectedCode, setSelectedCode] = useState(null);
  const [communeDetail, setCommuneDetail] = useState(null);
  const [pige, setPige] = useState([]);
  const [audit, setAudit] = useState([]);

  const loadDashboard = async () => {
    setIsLoading(true);
    try {
      const [nextOverview, nextOpportunities, nextNodes, nextMap, nextPige, nextAudit] = await Promise.all([
        getMandateOverview(),
        getMandateOpportunities(minimumScore),
        getMandateWorkflow(),
        getMandateMap(),
        getMandatePige(),
        getMandateAudit(),
      ]);
      setOverview(nextOverview);
      setOpportunities(nextOpportunities);
      setNodes(nextNodes);
      setMapCommunes(nextMap);
      setPige(nextPige);
      setAudit(nextAudit);
    } catch {
      toast.error("Impossible de charger le dashboard.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectCommune = async (communeCode) => {
    setSelectedCode(communeCode);
    try {
      setCommuneDetail(await getMandateCommune(communeCode));
    } catch {
      toast.error("Impossible de charger la fiche commune.");
    }
  };

  const handleCloseDetail = () => {
    setSelectedCode(null);
    setCommuneDetail(null);
  };

  useEffect(() => {
    loadDashboard();
  }, [minimumScore]);

  const handleRun = async () => {
    setIsRunning(true);
    try {
      const result = await runMandateWorkflow();
      toast.success(result.message);
      await loadDashboard();
    } catch {
      toast.error("La vérification des sources n’a pas abouti.");
    } finally {
      setIsRunning(false);
    }
  };

  const handleApprove = async (signalId) => {
    try {
      await approveMandateOpportunity(signalId);
      toast.success("Opportunité approuvée — alerte interne envoyée.");
      loadDashboard();
    } catch {
      toast.error("Impossible d’approuver cette opportunité.");
    }
  };

  const handleReject = async (signalId, reason) => {
    try {
      await rejectMandateOpportunity(signalId, reason);
      toast.success("Opportunité rejetée — motif journalisé pour l’audit.");
      loadDashboard();
      return true;
    } catch {
      toast.error("Impossible de rejeter cette opportunité.");
      return false;
    }
  };

  const handlePipeline = async (signalId, stage) => {
    try {
      await setMandatePipeline(signalId, stage);
      toast.success(stage === "aucun" ? "Suivi retiré." : "Statut de suivi mis à jour.");
      loadDashboard();
    } catch {
      toast.error("Impossible de mettre à jour le suivi.");
    }
  };

  const handleSendDigest = async () => {
    setIsSendingDigest(true);
    try {
      const result = await sendMandateDigest();
      toast.success(
        result.sent
          ? `Digest envoyé : ${result.pige_count || 0} annonce(s) Pige en tête + ${result.opportunity_count - (result.pige_count || 0)} signal(aux) 60-79.`
          : result.reason || "Aucune opportunité à envoyer.",
      );
    } catch {
      toast.error("L’envoi du digest a échoué.");
    } finally {
      setIsSendingDigest(false);
    }
  };

  const handleExportTour = async (communeCode) => {
    setIsExporting(true);
    try {
      await downloadMandateTourPdf(communeCode);
      toast.success("Fiche de tournée téléchargée.");
    } catch {
      toast.error("La génération du PDF a échoué.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleLogout = async () => {
    await logoutDashboard();
    onLogout();
  };

  return (
    <main className="mandate-dashboard" data-testid="mandate-dashboard-page">
      <header className="mandate-dashboard-header">
        <div>
          <p className="mandate-overline">Pimp My Place · Machine à Mandats</p>
          <h1>Les signaux qui méritent un regard.</h1>
        </div>
        <div className="mandate-user-actions">
          <span data-testid="dashboard-user-email">{user.email}</span>
          <button onClick={handleLogout} data-testid="dashboard-logout-button" aria-label="Se déconnecter">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <section className="mandate-command-bar" data-testid="mandate-command-bar">
        <div><Radar size={20} /><span>Rayon 30 km · L’Isle-sur-la-Sorgue</span></div>
        <div className="mandate-command-actions">
          <button onClick={loadDashboard} data-testid="dashboard-refresh-button">
            <RefreshCw size={16} /> Actualiser
          </button>
          <button
            onClick={handleSendDigest}
            disabled={isSendingDigest}
            data-testid="dashboard-send-digest-button"
          >
            <Mail size={16} /> {isSendingDigest ? "Envoi…" : "Envoyer le digest"}
          </button>
          <button
            onClick={() => handleExportTour(null)}
            disabled={isExporting}
            data-testid="dashboard-tour-pdf-button"
          >
            <FileDown size={16} /> {isExporting ? "Génération…" : "Tournée du jour (PDF)"}
          </button>
          <button
            className="mandate-primary-button"
            onClick={handleRun}
            disabled={isRunning}
            data-testid="dashboard-run-workflow-button"
          >
            <Play size={16} /> {isRunning ? "Collecte lancée…" : "Lancer la collecte"}
          </button>
        </div>
      </section>

      <section className="mandate-kpis" data-testid="mandate-kpis">
        <article><Radar size={21} /><span>Opportunités ≥ 60</span><strong data-testid="dashboard-opportunity-count">{overview?.total_opportunities ?? "—"}</strong></article>
        <article><CircleAlert size={21} /><span>Priorités ≥ 80</span><strong data-testid="dashboard-priority-count">{overview?.priority_opportunities ?? "—"}</strong></article>
        <article><Database size={21} /><span>Sources actives</span><strong data-testid="dashboard-source-count">{overview?.sources_enabled?.length ?? "—"}</strong></article>
        <article><Activity size={21} /><span>Dernière donnée</span><strong className="mandate-date" data-testid="dashboard-latest-update">{formatDate(overview?.latest_update)}</strong></article>
      </section>

      <section className="mandate-map-section" data-testid="mandate-map-section">
        <div className="mandate-section-heading">
          <div>
            <p className="mandate-overline">Carte des alertes</p>
            <h2>Le Vaucluse en un regard</h2>
          </div>
        </div>
        <div className={`mandate-map-workspace ${communeDetail ? "" : "no-detail"}`}>
          <MandateMap communes={mapCommunes} selectedCode={selectedCode} onSelect={handleSelectCommune} />
          {communeDetail && <CommuneDetail detail={communeDetail} onClose={handleCloseDetail} onExportPdf={handleExportTour} />}
        </div>
      </section>

      <PigeListings listings={pige} />
      <CardQrSection />

      <AuditLog entries={audit} />

      <section className="mandate-workspace">
        <div className="mandate-feed">
          <div className="mandate-section-heading">
            <div><p className="mandate-overline">Opportunités</p><h2>À examiner</h2></div>
            <div className="mandate-score-filter" data-testid="dashboard-score-filter">
              <button className={minimumScore === 60 ? "is-active" : ""} onClick={() => setMinimumScore(60)} data-testid="dashboard-score-60-button">≥ 60</button>
              <button className={minimumScore === 80 ? "is-active" : ""} onClick={() => setMinimumScore(80)} data-testid="dashboard-score-80-button">≥ 80</button>
            </div>
          </div>
          <OpportunityList
            isLoading={isLoading}
            opportunities={opportunities}
            highlightId={highlightId}
            onApprove={handleApprove}
            onReject={handleReject}
            onPipeline={handlePipeline}
          />
        </div>

        <aside className="mandate-nodes" data-testid="dashboard-workflow-nodes">
          <div className="mandate-section-heading"><div><p className="mandate-overline">Workflow</p><h2>{nodes.length || 13} nœuds</h2></div></div>
          <WorkflowNodeList nodes={nodes} />
        </aside>
      </section>
    </main>
  );
}